(() => {
  if (customElements.get("vp-pdp-sticky-nav")) return;

  class VpPdpStickyNav extends HTMLElement {
    constructor() {
      super();
      this.links = [];
      this.targets = [];
      this.firstTarget = null;
      this.navInner = null;
      this.rafId = null;
      this.currentTopOffset = 0;
      this.mediaElement = null;
      this.hideBeforeSticky = this.dataset.hideBeforeFirstTarget === "true";
      this.activeIndex = -1;
      this.isPointerDown = false;
      this.isDraggingNav = false;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this._suppressNavScrollIntoView = true;
      this._initialSyncScheduled = false;
      this.lastScrollY = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onLinkClick = this.onLinkClick.bind(this);
      this.onGlobalInteraction = this.onGlobalInteraction.bind(this);
      this.onNavMouseDown = this.onNavMouseDown.bind(this);
      this.onNavMouseMove = this.onNavMouseMove.bind(this);
      this.onNavMouseUp = this.onNavMouseUp.bind(this);
      this.onNavClickCapture = this.onNavClickCapture.bind(this);
    }

    connectedCallback() {
      // this.navInner = this.querySelector(".vp-pdp-sticky-nav__inner");
      this.navInner = this.querySelector(".vp-pdp-sticky-nav");
      this.navList = /** @type {HTMLElement|null} */ (this.querySelector(".vp-pdp-sticky-nav__list"));
      this.links = Array.from(this.querySelectorAll(".vp-pdp-sticky-nav__link"));

      if (!this.links.length) return;

      this.mapTargets();
      if (!this.targets.length) return;

      this.firstTarget = this.targets[0] ? this.targets[0].element : null;
      this.mediaElement = this.findMediaElement();
      this.attachEvents();
      this.updateStickyPosition();
      this.updateStickyState();
      this.updateOverlayState();
      this.updateMediaOffset();
      this.scheduleInitialSyncAfterScrollRestoration();
      this.lastScrollY = document.body.scrollTop;
    }

    disconnectedCallback() {
      const parent = this.parentElement;
      if (parent instanceof HTMLElement) {
        parent.classList.remove("is-scrolling-down");
      }
      this.detachEvents();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    /**
     * Resolve one nav item's target setting to an element.
     *
     * A bare name has always meant a class here, so that is still tried first and every
     * existing nav keeps resolving exactly as it did. Falling back to an id afterwards is
     * what lets a section be targeted by `#some-id`, and writing the selector out with a
     * leading `.` or `#` skips the guessing entirely.
     *
     * @param {string} value
     * @returns {Element|null}
     */
    resolveTarget(value) {
      const raw = (value || "").trim();
      if (!raw) return null;

      const candidates =
        raw.startsWith(".") || raw.startsWith("#")
          ? [raw]
          : [`.${raw}`, `#${raw}`, `#${CSS.escape(raw)}`];

      for (const selector of candidates) {
        try {
          const element = document.querySelector(selector);
          if (element) return element;
        } catch (error) {
          // Not a valid selector in this form — an id starting with a digit, say. Try the next.
        }
      }

      return null;
    }

    mapTargets() {
      const mappedTargets = [];
      const validLinks = [];

      this.links.forEach((link) => {
        const target = (link.dataset.targetClass || "").trim();
        const navItem = link.closest(".vp-pdp-sticky-nav__item");

        if (!target) {
          if (navItem instanceof HTMLElement) navItem.hidden = true;
          return;
        }

        const element = this.resolveTarget(target);
        const hasTarget = Boolean(element);

        if (navItem instanceof HTMLElement) navItem.hidden = !hasTarget;
        link.setAttribute("aria-hidden", hasTarget ? "false" : "true");
        link.tabIndex = hasTarget ? 0 : -1;

        if (!element) return;

        mappedTargets.push({ element, link });
        validLinks.push(link);
      });

      this.targets = mappedTargets;
      this.links = validLinks;
    }

    attachEvents() {
      document.body.addEventListener("scroll", this.onScroll, { passive: true });
      document.body.addEventListener("resize", this.onResize, { passive: true });
      document.body.addEventListener("load", this.onResize);
      document.body.addEventListener("pageshow", this.onPageShow);
      document.addEventListener("click", this.onGlobalInteraction, true);
      document.addEventListener("keyup", this.onGlobalInteraction, true);
      this.links.forEach((link) => link.addEventListener("click", this.onLinkClick));
      if (this.navList) {
        this.navList.addEventListener("mousedown", this.onNavMouseDown);
        this.navList.addEventListener("mousemove", this.onNavMouseMove);
        this.navList.addEventListener("mouseleave", this.onNavMouseUp);
        document.body.addEventListener("mouseup", this.onNavMouseUp);
        this.navList.addEventListener("click", this.onNavClickCapture, true);
      }
    }

    detachEvents() {
      document.body.removeEventListener("scroll", this.onScroll);
      document.body.removeEventListener("resize", this.onResize);
      document.body.removeEventListener("load", this.onResize);
      document.body.removeEventListener("pageshow", this.onPageShow);
      document.body.removeEventListener("click", this.onGlobalInteraction, true);
      document.body.removeEventListener("keyup", this.onGlobalInteraction, true);
      this.links.forEach((link) => link.removeEventListener("click", this.onLinkClick));
      if (this.navList) {
        this.navList.removeEventListener("mousedown", this.onNavMouseDown);
        this.navList.removeEventListener("mousemove", this.onNavMouseMove);
        this.navList.removeEventListener("mouseleave", this.onNavMouseUp);
        this.navList.removeEventListener("click", this.onNavClickCapture, true);
      }
      document.removeEventListener("mouseup", this.onNavMouseUp);
    }

    onPageShow = () => {
      requestAnimationFrame(() => {
        this.updateStickyPosition();
        this.updateStickyState();
        this.updateActiveFromScroll();
        this.updateOverlayState();
        this.updateMediaOffset();
      });
    };

    scheduleInitialSyncAfterScrollRestoration() {
      if (this._initialSyncScheduled) return;
      this._initialSyncScheduled = true;

      const run = () => {
        this._suppressNavScrollIntoView = false;
        this.updateStickyPosition();
        this.updateStickyState();
        this.updateActiveFromScroll();
        this.updateOverlayState();
        this.updateMediaOffset();
      };

      const schedule = () => {
        requestAnimationFrame(() => requestAnimationFrame(run));
      };

      if (document.readyState === "complete") {
        schedule();
      } else {
        window.addEventListener(
          "load",
          () => {
            schedule();
          },
          { once: true },
        );
      }
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.updateStickyPosition();
        this.updateStickyState();
        this.updateActiveFromScroll();
        this.updateOverlayState();
        this.updateScrollDirectionOnParent();
        this.rafId = null;
      });
    }

    onResize() {
      this.updateStickyPosition();
      this.updateStickyState();
      this.updateActiveFromScroll();
      this.updateOverlayState();
      this.updateMediaOffset();
      this.lastScrollY = window.scrollY;
    }

    /** Adds `is-scrolling-down` on the theme block wrapper while the window scroll position moves downward. */
    updateScrollDirectionOnParent() {
      const parent = this.parentElement;
      if (!(parent instanceof HTMLElement)) return;

      const y = document.body.scrollTop;
      if (y > this.lastScrollY && y > 0) {
        parent.classList.add("is-scrolling-down");
      } else if (y < this.lastScrollY || y <= 0) {
        parent.classList.remove("is-scrolling-down");
      }
      this.lastScrollY = y;
    }

    onGlobalInteraction() {
      requestAnimationFrame(() => {
        this.updateStickyPosition();
        this.updateOverlayState();
      });
    }

    /** @param {MouseEvent} event */
    onNavMouseDown(event) {
      // Drag-to-scroll is a desktop-only interaction.
      if (!(event instanceof MouseEvent) || event.button !== 0) return;
      if (!this.navList || this.navList.scrollWidth <= this.navList.clientWidth) return;

      this.isPointerDown = true;
      this.isDraggingNav = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.navList.scrollLeft;
      this.navList.style.cursor = "grabbing";
      this.navList.style.userSelect = "none";
    }

    /** @param {MouseEvent} event */
    onNavMouseMove(event) {
      if (!this.isPointerDown || !this.navList) return;

      const deltaX = event.clientX - this.dragStartX;
      if (!this.isDraggingNav && Math.abs(deltaX) > 3) {
        this.isDraggingNav = true;
      }

      if (!this.isDraggingNav) return;
      event.preventDefault();
      this.navList.scrollLeft = this.dragStartScrollLeft - deltaX;
    }

    onNavMouseUp() {
      if (!this.navList) return;
      this.isPointerDown = false;
      this.navList.style.cursor = "";
      this.navList.style.userSelect = "";
    }

    /** @param {MouseEvent} event */
    onNavClickCapture(event) {
      if (!this.isDraggingNav) return;
      event.preventDefault();
      event.stopPropagation();
      this.isDraggingNav = false;
    }

    /** @param {MouseEvent} event */
    onLinkClick(event) {
      event.preventDefault();
      this._suppressNavScrollIntoView = false;
      const link = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
      if (!link) return;
      const target = this.resolveTarget(link.dataset.targetClass);
      if (!target) return;

      this.updateStickyPosition();
      this.updateStickyState();
      const y = target.getBoundingClientRect().top + document.body.scrollTop + 5;
      document.body.scrollTo({ top: Math.max(y, 0), behavior: "smooth" });
    }

    isMobileViewport() {
      return window.matchMedia("(max-width: 989px)").matches;
    }

    getVisibleBottom(element) {
      if (!element) return 0;
      const rect = element.getBoundingClientRect();
      if (rect.height <= 0) return 0;
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0;
      return Math.max(rect.bottom, 0);
    }

    getHeaderOffset(headerCandidates) {
      let topOffset = 0;

      for (const selector of headerCandidates) {
        const headers = document.querySelectorAll(selector);
        if (!headers.length) continue;

        headers.forEach((header) => {
          topOffset = Math.max(topOffset, this.getVisibleBottom(header));
        });
      }

      return topOffset;
    }

    getTopOffset() {
      if (this.isMobileViewport()) {
        const mobileHeaderCandidates = ["#header-group"];

        return this.getHeaderOffset(mobileHeaderCandidates);
      }

      const desktopHeaderCandidates = ["#header-group"];
      return this.getHeaderOffset(desktopHeaderCandidates);
    }

    findMediaElement() {
      let media = null;

      const closestSection = this.closest(".product-information");
      if (closestSection instanceof HTMLElement) {
        media = closestSection.querySelector(".product-information__media");
      }

      if (!media) {
        media = document.querySelector(".product-information__media");
      }

      return media instanceof HTMLElement ? media : null;
    }

    isAnyOverlayOpen() {
      const body = document.body;
      if (!body) return false;

      const bodyOpenClasses = [
        "overflow-hidden",
        "menu-open",
        "menu-drawer-open",
        "drawer-open",
        "cart-open",
        "cart-drawer-open",
        "search-open",
        "search-modal-open",
        "modal-open",
      ];
      if (bodyOpenClasses.some((className) => body.classList.contains(className))) return true;

      const overlaySelector = [
        ".menu-drawer[open]",
        ".menu-drawer.is-open",
        ".menu-opening",
        ".cart-drawer[open]",
        ".cart-drawer.is-open",
        ".cart-drawer.active",
        ".search-modal[open]",
        ".search-modal.is-open",
        ".predictive-search[open]",
        ".predictive-search--open",
        ".drawer[open]",
        ".drawer.is-open",
        ".modal[open]",
        ".modal.is-open",
        ".modal.active",
        "dialog[open]",
      ].join(",");

      return Boolean(document.querySelector(overlaySelector));
    }

    getNavHeight() {
      return this.navInner ? this.navInner.offsetHeight : this.offsetHeight;
    }

    getFirstTargetEndY() {
      if (!this.firstTarget) return 0;
      if (this.isMobileViewport()) {
        return this.firstTarget.offsetTop + this.firstTarget.offsetHeight;
      } else {
        return this.firstTarget.offsetTop + this.firstTarget.offsetHeight;
      }
    }

    updateStickyPosition() {
      const topOffset = this.getTopOffset();
      this.currentTopOffset = topOffset;
      this.style.setProperty("--vp-sticky-top", `${topOffset}px`);

      const hasMobileHeaderOffset = this.isMobileViewport() && topOffset > 0;
      this.classList.toggle("is-mobile-under-header", hasMobileHeaderOffset);
      this.classList.toggle("is-mobile-top-edge", this.isMobileViewport() && !hasMobileHeaderOffset);
    }

    updateOverlayState() {
      const shouldLowerZIndex = this.isMobileViewport() && this.isAnyOverlayOpen();
      this.classList.toggle("is-overlay-open", shouldLowerZIndex);
    }

    updateMediaOffset() {
      if (!this.mediaElement) return;

      const isMobile = this.isMobileViewport();
      const hasNavInner = !!this.navInner;

      if (isMobile && hasNavInner) {
        const offset = 32;
        this.mediaElement.style.marginTop = `${offset}px`;
      } else {
        this.mediaElement.style.marginTop = "";
      }
    }

    updateStickyState() {
      const currentY = document.body.scrollTop + this.currentTopOffset;
      const startAt = this.getFirstTargetEndY();
      const shouldStick = currentY >= startAt;

      this.classList.toggle("is-sticky", shouldStick);

      if (this.hideBeforeSticky) {
        this.classList.toggle("is-hidden-before-sticky", !shouldStick);
      }
    }

    updateActiveFromScroll() {
      if (!this.targets.length) return;

      const viewportTop = this.currentTopOffset + this.getNavHeight();
      const viewportBottom = window.innerHeight;
      const maxRemainingRatio = 0.1;

      const metrics = [];

      for (let i = 0; i < this.targets.length; i += 1) {
        const target = this.targets[i];
        if (!target) continue;

        const rect = target.element.getBoundingClientRect();
        const visibleTop = Math.max(rect.top, viewportTop);
        const visibleBottom = Math.min(rect.bottom, viewportBottom);
        const visible = Math.max(0, visibleBottom - visibleTop);
        const height = Math.max(rect.height, 1);
        metrics.push({ index: i, visible, height });
      }

      let bestIndex = -1;

      for (let m = 0; m < metrics.length; m += 1) {
        const row = metrics[m];
        if (!row || row.visible <= 0) continue;
        if (row.visible / row.height > maxRemainingRatio) {
          bestIndex = row.index;
          break;
        }
      }

      if (bestIndex < 0) {
        for (let m = 0; m < metrics.length; m += 1) {
          const row = metrics[m];
          if (row && row.visible > 0) {
            bestIndex = row.index;
            break;
          }
        }
      }

      if (bestIndex < 0) {
        this.setActive(-1);
        return;
      }

      const targetItem = this.targets[bestIndex];
      if (!targetItem) return;
      const linkIndex = this.links.indexOf(targetItem.link);
      if (linkIndex > -1) this.setActive(linkIndex);
    }

    setActive(index) {
      if (this.activeIndex === index) return;
      this.activeIndex = index;
      this.links.forEach((link, i) => link.classList.toggle("is-active", i === index));
      this.scrollActiveIntoView();
    }

    scrollActiveIntoView() {
      if (this._suppressNavScrollIntoView) return;
      if (!this.navList) return;
      const activeLink = this.navList.querySelector(".vp-pdp-sticky-nav__link.is-active");
      if (!(activeLink instanceof HTMLElement)) return;

      const containerWidth = this.navList.clientWidth;
      const maxScrollLeft = Math.max(this.navList.scrollWidth - containerWidth, 0);
      const rawTargetLeft = activeLink.offsetLeft - (containerWidth - activeLink.offsetWidth) / 2;
      const nextScrollLeft = Math.min(Math.max(rawTargetLeft, 0), maxScrollLeft);

      if (Math.abs(this.navList.scrollLeft - nextScrollLeft) < 1) return;

      // Instant scroll avoids interaction with document scroll-behavior: smooth and load/restoration.
      this.navList.scrollTo({
        left: nextScrollLeft,
        behavior: "auto",
      });
    }
  }

  customElements.define("vp-pdp-sticky-nav", VpPdpStickyNav);
})();
